<template>
  <div class="modal-overlay">
    <div class="cc-add-contact-dialog" @click.stop>
      <!-- Close button -->
      <button class="modal-close-btn" @click="$emit('cancel')" type="button" aria-label="Close">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </button>

      <div class="form-section">
        <h3>{{ t(isEdit ? 'consultantCorner.addContact.editTitle' : 'consultantCorner.addContact.title') }}</h3>
        <hr>
        <h3 class="section-title">{{ t('address.Name') }}</h3>

        <div class="cc-name-group">
          <label class="form-label">{{ t('consultantCorner.addContact.firstName') }} <span class="required">*</span><span class="sr-only">{{ t('accessibility.required') }}</span></label>
          <div class="cc-name-inputs">
            <div class="cc-name-field">
              <input class="k-textbox" :class="{ 'error-input': firstNameError }" :placeholder="t('consultantCorner.addContact.firstName')" v-model="contact.firstName" @blur="validateFirstName" @input="clearFieldError('firstName')" />
              <div v-if="firstNameError" class="k-form-error">{{ firstNameError }}</div>
            </div>
            <div class="cc-name-field">
              <input class="k-textbox" :class="{ 'error-input': lastNameError }" :placeholder="t('consultantCorner.addContact.lastName')" v-model="contact.lastName" @blur="validateLastName" @input="clearFieldError('lastName')" />
              <div v-if="lastNameError" class="k-form-error">{{ lastNameError }}</div>
            </div>
          </div>
        </div>
        <hr>
        <div class="row mb-3">
          <div class="col-12">
            <label class="form-label">{{ t('consultantCorner.addContact.preferredName') }}</label>
          </div>
          <div class="col-12">
            <input class="k-textbox" :placeholder="t('consultantCorner.addContact.preferredName')" v-model="contact.preferredName" />
          </div>
        </div>
        <hr>
        <AddressControl
          :name="contact.firstName + ' ' + contact.lastName"
          :countryCode="contact.shippingAddress.countryCode"
          :addressLine1="contact.shippingAddress.addressLine1"
          :addressLine2="contact.shippingAddress.addressLine2"
          :houseNumber="contact.shippingAddress.houseNumber || ''"
          :city="contact.shippingAddress.city"
          :stateCode="contact.shippingAddress.stateCode"
          :postalCode="contact.shippingAddress.postalCode"
          :isdisabled="false"
          columnClass="col-12"
          :showfullnamefield="false"
          @address-change="handleShippingAddressChange"
        />
        <hr>
      </div>


      <div>
        <div class="section-title">{{ t('consultantCorner.addContact.address.billing') }}</div>
        <div class="checkbox-row">
          <KCheckBox
            v-model="sameAsDelivery"
            :label="t('consultantCorner.addContact.address.sameAsDelivery')"
          />
        </div>
      </div>

      <section v-if="!sameAsDelivery" class="mt-3">
        <AddressControl
          :name="contact.firstName + ' ' + contact.lastName"
          :countryCode="contact.billingAddress.countryCode"
          :addressLine1="contact.billingAddress.addressLine1"
          :addressLine2="contact.billingAddress.addressLine2"
          :houseNumber="contact.billingAddress.houseNumber || ''"
          :city="contact.billingAddress.city"
          :stateCode="contact.billingAddress.stateCode"
          :postalCode="contact.billingAddress.postalCode"
          :isdisabled="false"
          columnClass="col-12"
          :showfullnamefield="false"
          @address-change="handleBillingAddressChange"
        />
      </section>

      <section>
        <div class="section-title">{{ t('consultantCorner.editContact.Communication') }}</div>
        <div class="row mb-3">
          <div class="col-12">
            <label class="form-label">{{ t('consultantCorner.addContact.email') }} <span class="required">*</span><span class="sr-only">{{ t('accessibility.required') }}</span></label>
          </div>
          <div class="col-12">
            <input class="k-textbox" :class="{ 'error-input': emailError }" :placeholder="t('consultantCorner.addContact.email')" v-model="contact.emailAddress" :disabled="hasNoEmail" @blur="validateEmail" @input="clearFieldError('email')" />
            <div v-if="emailError" class="k-form-error">{{ emailError }}</div>
          </div>
        </div>

        <div class="row mb-3">
          <div class="col-12">
            <KCheckBox
              class="chbx-no-email"
              :modelValue="hasNoEmail"
              :label="t('consultantCorner.addContact.noEmail')"
              @change="noEmailCheckboxHandler($event)"
            />
          </div>
        </div>

        <div class="row mb-3">
          <div class="col-12">
            <label class="form-label">{{ t('consultantCorner.addContact.phone.primary') }} <span class="required">*</span><span class="sr-only">{{ t('accessibility.required') }}</span></label>
          </div>
          <div class="col-12">
            <PhoneInputControl
              ref="primaryPhoneControl"
              v-model="contact.primaryPhone"
              :required="true"
              :showExtension="true"
              :showPhoneType="true"
              :countryList="phoneCountryOptions"
              :placeholder="t('consultantCorner.addContact.phone.numberPlaceholder')"
              @validation="handlePrimaryPhoneValidation"
            />
          </div>
        </div>

        <div class="row mb-3">
          <div class="col-12">
            <label class="form-label">{{ t('consultantCorner.addContact.phone.alternative') }}</label>
          </div>
          <div class="col-12">
            <PhoneInputControl
              ref="altPhone1Control"
              v-model="contact.altPhone1"
              :required="false"
              :showExtension="true"
              :showPhoneType="true"
              :countryList="phoneCountryOptions"
              :placeholder="t('consultantCorner.addContact.phone.numberPlaceholder')"
            />
          </div>
        </div>
      </section>

      <div class="button-container">
        <button class="k-button" @click="$emit('cancel')">{{ t('consultantCorner.addContact.cancel') }}</button>
        <button class="k-button k-primary" :disabled="hasValidationErrors" @click="handleSubmit">{{ t('consultantCorner.addContact.save') }}</button>
      </div>
    </div>

    <!-- Address Verification Popup -->
    <div v-if="addressVerificationShow" class="verify-address-overlay" @click.self="addressVerificationShow = false">
      <div class="verify-address-modal" @click.stop>
        <VerifyAddressPopup
          :enteredAddress="{
            firstName: contact.firstName,
            lastName: contact.lastName,
            addressLine1: isVerifyingShipping ? contact.shippingAddress.addressLine1 : contact.billingAddress.addressLine1,
            addressLine2: isVerifyingShipping ? contact.shippingAddress.addressLine2 : contact.billingAddress.addressLine2,
            houseNumber: isVerifyingShipping ? contact.shippingAddress.houseNumber : contact.billingAddress.houseNumber,
            city: isVerifyingShipping ? contact.shippingAddress.city : contact.billingAddress.city,
            stateCode: isVerifyingShipping ? contact.shippingAddress.stateCode : contact.billingAddress.stateCode,
            postalCode: isVerifyingShipping ? contact.shippingAddress.postalCode : contact.billingAddress.postalCode,
            countryCode: isVerifyingShipping ? contact.shippingAddress.countryCode : contact.billingAddress.countryCode,
          }"
          :verifyAddress="verifyAddress"
          :verifyAddressError="addressVerifyError"
          @use-entered-address="useEnteredAddressClick"
          @use-selected-address="useSelectedAddressClick"
          @re-enter-address="addressVerificationShow = false"
        >
        </VerifyAddressPopup>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { reactive, watch, ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { Checkbox as KCheckBox, type CheckboxChangeEvent } from '@progress/kendo-vue-inputs'
import type { GridContact } from '../../types/AddressBookTypes'
import type { IAddressCreate, IAddress, IAddressVerificationListItem } from '../../types/CountryCultureTypes'
import type { IPhoneOutput } from '../../types/CommonTypes'
import { AddressAdd } from '../../services/EventService'
import { AddressBookCreate } from '../../services/AddressBookService'
import { UpdateAddressBook } from '../../api/AddressBook'
import AddressControl from '../PWS/AddressControl.vue'
import VerifyAddressPopup from '../VerifyAddressPopup.vue'
import PhoneInputControl from '../PhoneInputControl.vue'
import { createNameFieldValidator, createRequiredFieldValidator, createEmailFieldValidator } from '../../validators/StringValidator'
import { useLogger } from 'vue-logger-plugin'

const logger = useLogger()

// Define a more specific type that makes optional fields required in our component
type ContactForm = Omit<GridContact, 'altPhone1'> & {
  altPhone1: IPhoneOutput // Make altPhone1 required
}

const { t } = useI18n()

const props = defineProps<{
  modelValue: GridContact,
  isEdit?: boolean
}>()

const emit = defineEmits<{
  (e: 'cancel'): void
  (e: 'submit', contact: GridContact): void
  (e: 'success', contact: GridContact): void
}>()

// Initialize with default empty contact if not editing
const defaultContact: ContactForm = {
  addressBookId: 0,
  firstName: '',
  lastName: '',
  emailAddress: '',
  preferredName: '',
  shippingAddress: {
    countryCode: '',
    firstName: '',
    lastName: '',
    addressLine1: '',
    addressLine2: '',
    stateCode: '',
    city: '',
    postalCode: '',
    houseNumber: null,
    addressId: null,
  },
  billingAddress: {
    countryCode: '',
    firstName: '',
    lastName: '',
    addressLine1: '',
    addressLine2: '',
    stateCode: '',
    city: '',
    postalCode: '',
    houseNumber: null,
    addressId: null,
  },
  primaryPhone: {
    number: '',
    countryCode: '',
    codeIdPhoneType: '',
    extension: '',
    formattedNumber: ''
  },
  altPhone1: {
    number: '',
    countryCode: '',
    codeIdPhoneType: '',
    extension: '',
    formattedNumber: ''
  },
  selected: false,
  actions: ''
}

console.log('=== ADD/EDIT CONTACT FORM INITIALIZATION ===');
console.log('Props modelValue:', props.modelValue);
console.log('Is Edit Mode:', props.isEdit);

// When editing, ensure the contact has all required fields, otherwise use default
const contact = reactive<ContactForm>(props.modelValue ? {
  ...props.modelValue,
  // For edit mode, ensure primary phone has type set and alt phone is empty
  primaryPhone: {
    ...props.modelValue.primaryPhone,
    codeIdPhoneType: props.modelValue.primaryPhone?.codeIdPhoneType || ''
  },
  altPhone1: {
    number: '',
    formattedNumber: '',
    countryCode: '',
    codeIdPhoneType: '',
    extension: ''
  }
} : defaultContact)

console.log('xxInitialized contact:', contact);
console.log('xxShipping address:', contact.shippingAddress);
console.log('xxBilling address:', contact.billingAddress);

const sameAsDelivery = ref(true)
const hasNoEmail = ref(false)

// Validation error refs
const firstNameError = ref('')
const lastNameError = ref('')
const emailError = ref('')
const phoneError = ref('')

// Validator instances
const firstNameValidators = [createRequiredFieldValidator(t, 'firstName'), createNameFieldValidator(t, 'firstName')]
const lastNameValidators = [createRequiredFieldValidator(t, 'lastName'), createNameFieldValidator(t, 'lastName')]
const emailValidators = [createRequiredFieldValidator(t, 'emailAddress'), createEmailFieldValidator(t, 'emailAddress')]

// Computed: true when any required field is empty or invalid
const hasValidationErrors = computed(() => {
  if (!contact.firstName || !contact.lastName || !contact.emailAddress) return true

  const firstNameHasError = firstNameValidators.some(v => !!(v as (val: string) => string)(contact.firstName))
  const lastNameHasError = lastNameValidators.some(v => !!(v as (val: string) => string)(contact.lastName))
  const emailHasError = emailValidators.some(v => !!(v as (val: string) => string)(contact.emailAddress))

  if (firstNameHasError || lastNameHasError || emailHasError) return true
  if (phoneError.value) return true
  if (!contact.primaryPhone.number || !contact.primaryPhone.countryCode) return true

  return false
})

// Field validation functions
function validateFirstName() {
  for (const v of firstNameValidators) {
    const error = (v as (val: string) => string)(contact.firstName)
    if (error) { firstNameError.value = error; return false }
  }
  firstNameError.value = ''
  return true
}

function validateLastName() {
  for (const v of lastNameValidators) {
    const error = (v as (val: string) => string)(contact.lastName)
    if (error) { lastNameError.value = error; return false }
  }
  lastNameError.value = ''
  return true
}

function validateEmail() {
  for (const v of emailValidators) {
    const error = (v as (val: string) => string)(contact.emailAddress)
    if (error) { emailError.value = error; return false }
  }
  emailError.value = ''
  return true
}

function clearFieldError(field: string) {
  if (field === 'firstName') firstNameError.value = ''
  else if (field === 'lastName') lastNameError.value = ''
  else if (field === 'email') emailError.value = ''
}

function handlePrimaryPhoneValidation(isValid: boolean, errorMessage: string) {
  phoneError.value = isValid ? '' : errorMessage
}

// Country options for phone input (limited list)
const phoneCountryOptions = [
  { text: 'DE', id: 'DE' },
  { text: 'AT', id: 'AT' },
  { text: 'FR', id: 'FR' }
]

// Ref for phone control validation
const primaryPhoneControl = ref<InstanceType<typeof PhoneInputControl> | null>(null)
const altPhone1Control = ref<InstanceType<typeof PhoneInputControl> | null>(null)

// Address verification state
const verifyAddress = ref<IAddressVerificationListItem[]>([])
const addressVerificationShow = ref<boolean>(false)
const addressVerifyError = ref<boolean>(false)
const isVerifyingShipping = ref<boolean>(true) // Track which address is being verified

watch(sameAsDelivery, (val) => {
  if (val) {
    contact.billingAddress = {
      countryCode: contact.shippingAddress.countryCode,
      firstName: contact.shippingAddress.firstName,
      lastName: contact.shippingAddress.lastName,
      addressLine1: contact.shippingAddress.addressLine1,
      addressLine2: contact.shippingAddress.addressLine2,
      houseNumber: contact.shippingAddress.houseNumber,
      city: contact.shippingAddress.city,
      stateCode: contact.shippingAddress.stateCode,
      postalCode: contact.shippingAddress.postalCode,
      addressId: contact.shippingAddress.addressId
    }
  }
})

const handleShippingAddressChange = (address: any) => {
  console.log('=== SHIPPING ADDRESS CHANGE ===');
  console.log('Received address update:', address);

  contact.shippingAddress = {
    countryCode: address.countryCode,
    firstName: contact.firstName,
    lastName: contact.lastName,
    addressLine1: address.addressLine1,
    addressLine2: address.addressLine2 || '',
    stateCode: address.stateCode || '',
    city: address.city,
    postalCode: address.postalCode,
    houseNumber: address.houseNumber,
    addressId: address.addressId,
  }

  console.log('Updated shipping address:', contact.shippingAddress);

  if (sameAsDelivery.value) {
    contact.billingAddress = { ...contact.shippingAddress }
    console.log('Copied to billing address:', contact.billingAddress);
  }
}

const handleBillingAddressChange = (address: any) => {
  console.log('=== BILLING ADDRESS CHANGE ===');
  console.log('Received address update:', address);

  contact.billingAddress = {
    countryCode: address.countryCode,
    firstName: contact.firstName,
    lastName: contact.lastName,
    addressLine1: address.addressLine1,
    addressLine2: address.addressLine2 || '',
    stateCode: address.stateCode || '',
    city: address.city,
    postalCode: address.postalCode,
    houseNumber: address.houseNumber,
    addressId: address.addressId,
  }

  console.log('Updated billing address:', contact.billingAddress);
}

function generateSystemEmail() {
  const randomNumber = Math.floor(100000000 + Math.random() * 900000000)
  return `NoEmail_${randomNumber}@mailinator.com`
}

function noEmailCheckboxHandler(event: CheckboxChangeEvent) {
  hasNoEmail.value = !!event.value

  if (hasNoEmail.value) {
    contact.emailAddress = generateSystemEmail()
    emailError.value = ''
  } else {
    contact.emailAddress = ''
  }
}

// Address verification handlers
function useEnteredAddressClick() {
  logger.debug('useEnteredAddressClick')
  if (isVerifyingShipping.value) {
    addShippingAddress(true)
  } else {
    addBillingAddress(true)
  }
}

function useSelectedAddressClick(selectedAddress: IAddress) {
  logger.debug('useSelectedAddressClick', selectedAddress)

  if (isVerifyingShipping.value) {
    // Update shipping address with selected verified address
    contact.shippingAddress.addressLine1 = selectedAddress.addressLine1
    contact.shippingAddress.addressLine2 = selectedAddress.addressLine2 || ''
    contact.shippingAddress.houseNumber = selectedAddress.houseNumber || null
    contact.shippingAddress.city = selectedAddress.city
    contact.shippingAddress.stateCode = selectedAddress.stateCode || ''
    contact.shippingAddress.postalCode = selectedAddress.postalCode
    contact.shippingAddress.countryCode = selectedAddress.countryCode
    addShippingAddress(false)
  } else {
    // Update billing address with selected verified address
    contact.billingAddress.addressLine1 = selectedAddress.addressLine1
    contact.billingAddress.addressLine2 = selectedAddress.addressLine2 || ''
    contact.billingAddress.houseNumber = selectedAddress.houseNumber || null
    contact.billingAddress.city = selectedAddress.city
    contact.billingAddress.stateCode = selectedAddress.stateCode || ''
    contact.billingAddress.postalCode = selectedAddress.postalCode
    contact.billingAddress.countryCode = selectedAddress.countryCode
    addBillingAddress(false)
  }
}

async function addShippingAddress(skipAddressValidation: boolean) {
  verifyAddress.value = []
  const shippingAddressCreate: IAddressCreate = {
    address: {
      firstName: contact.shippingAddress.firstName || contact.firstName,
      lastName: contact.shippingAddress.lastName || contact.lastName,
      addressLine1: contact.shippingAddress.addressLine1,
      addressLine2: contact.shippingAddress.addressLine2 || null,
      houseNumber: contact.shippingAddress.houseNumber || null,
      city: contact.shippingAddress.city,
      stateCode: contact.shippingAddress.stateCode || null,
      postalCode: contact.shippingAddress.postalCode,
      countryCode: contact.shippingAddress.countryCode
    },
    skipAddressValidation: skipAddressValidation
  }

  const shippingAddressResponse = await AddressAdd(shippingAddressCreate)

  contact.shippingAddress.addressId = shippingAddressResponse.addressId

  // User explicitly chose to proceed with entered address — skip re-validation
  if (skipAddressValidation) {
    addressVerificationShow.value = false
    if (!sameAsDelivery.value) {
      isVerifyingShipping.value = false
      await addBillingAddress(false)
    } else {
      await finalizeContactSubmission()
    }
    return
  }

  for (const item of shippingAddressResponse.items) {
    verifyAddress.value.push(item)
  }

  if (shippingAddressResponse.addressValidationMessageId != undefined && shippingAddressResponse.addressValidationMessageId.length > 0) {
    addressVerifyError.value = true
  } else {
    addressVerifyError.value = false
  }

  if (verifyAddress.value.length > 0 || addressVerifyError.value == true) {
    addressVerificationShow.value = true
  } else {
    addressVerificationShow.value = false
    if (!sameAsDelivery.value) {
      isVerifyingShipping.value = false
      await addBillingAddress(false)
    } else {
      await finalizeContactSubmission()
    }
  }
}

async function addBillingAddress(skipAddressValidation: boolean) {
  verifyAddress.value = []
  const billingAddressCreate: IAddressCreate = {
    address: {
      firstName: contact.billingAddress.firstName || contact.firstName,
      lastName: contact.billingAddress.lastName || contact.lastName,
      addressLine1: contact.billingAddress.addressLine1,
      addressLine2: contact.billingAddress.addressLine2 || null,
      houseNumber: contact.billingAddress.houseNumber || null,
      city: contact.billingAddress.city,
      stateCode: contact.billingAddress.stateCode || null,
      postalCode: contact.billingAddress.postalCode,
      countryCode: contact.billingAddress.countryCode
    },
    skipAddressValidation: skipAddressValidation
  }

  const billingAddressResponse = await AddressAdd(billingAddressCreate)

  contact.billingAddress.addressId = billingAddressResponse.addressId

  // User explicitly chose to proceed with entered address — skip re-validation
  if (skipAddressValidation) {
    addressVerificationShow.value = false
    await finalizeContactSubmission()
    return
  }

  for (const item of billingAddressResponse.items) {
    verifyAddress.value.push(item)
  }

  if (billingAddressResponse.addressValidationMessageId != undefined && billingAddressResponse.addressValidationMessageId.length > 0) {
    addressVerifyError.value = true
  } else {
    addressVerifyError.value = false
  }

  if (verifyAddress.value.length > 0 || addressVerifyError.value == true) {
    addressVerificationShow.value = true
  } else {
    addressVerificationShow.value = false
    await finalizeContactSubmission()
  }
}

async function finalizeContactSubmission() {
  try {
    const shippingAddressId = contact.shippingAddress.addressId
    const billingAddressId = sameAsDelivery.value ? shippingAddressId : contact.billingAddress.addressId

    // Now create/update the contact with the address IDs
    const contactData = {
      firstName: contact.firstName,
      lastName: contact.lastName,
      emailAddress: contact.emailAddress,
      primaryPhone: {
        number: contact.primaryPhone.number || null,
        countryCode: contact.primaryPhone.countryCode || null,
        codeIdPhoneType: contact.primaryPhone.codeIdPhoneType || null
      },
      addressIdShipping: shippingAddressId,
      addressIdBilling: billingAddressId
    }

    let result;
    if (props.isEdit && contact.addressBookId) {
      // Update existing contact
      result = await UpdateAddressBook({
        ...contact,
        shippingAddress: {
          ...contact.shippingAddress,
          addressId: shippingAddressId || null
        },
        billingAddress: {
          ...contact.billingAddress,
          addressId: billingAddressId || null
        }
      })
    } else {
      // Create new contact
      result = await AddressBookCreate(contactData)
    }

    // Update the contact with the new ID and emit
    contact.addressBookId = result.addressBookId
    emit('submit', contact)
    emit('success', contact)
    emit('cancel')
  } catch (error) {
    logger.error('Error creating contact:', error)
  }
}

const isSubmitting = ref(false)

const handleSubmit = async () => {
  if (isSubmitting.value) return

  try {
    isSubmitting.value = true

    // Validate name and email with inline error messages
    const firstNameValid = validateFirstName()
    const lastNameValid = validateLastName()
    const emailValid = validateEmail()

    if (!firstNameValid || !lastNameValid || !emailValid) {
      isSubmitting.value = false
      return
    }

    // Validate address fields
    if (!contact.shippingAddress.addressLine1 || !contact.shippingAddress.city || !contact.shippingAddress.postalCode || !contact.shippingAddress.countryCode) {
      isSubmitting.value = false
      return
    }

    // Validate primary phone
    const primaryPhoneValid = primaryPhoneControl.value ? primaryPhoneControl.value.validate() : false
    if (!primaryPhoneValid) {
      isSubmitting.value = false
      return
    }

    // Also check that required phone fields are filled
    if (!contact.primaryPhone.countryCode || !contact.primaryPhone.number || !contact.primaryPhone.codeIdPhoneType) {
      if (primaryPhoneControl.value) primaryPhoneControl.value.validate()
      isSubmitting.value = false
      return
    }

    // Validate alt phone only if it has data
    if (altPhone1Control.value && contact.altPhone1.number) {
      const altPhoneValid = altPhone1Control.value.validate()
      if (!altPhoneValid) {
        isSubmitting.value = false
        return
      }
    }

    // All validation passed — proceed with address verification
    isVerifyingShipping.value = true
    await addShippingAddress(false)

  } catch (error) {
    logger.error('Error creating contact:', error)
    isSubmitting.value = false
  } finally {
    isSubmitting.value = false
  }
}
</script>

<style lang="scss">
.cc-add-contact-dialog {
  display: block;
  background: white;
  padding: 1.5rem;
  border-radius: $radius-md;
  width: 700px;
  max-width: 95%;
  max-height: 90vh;
  overflow-y: auto;
  margin: auto;
  position: relative;
}

.modal-close-btn {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6c757d;
  transition: color 0.2s ease, background-color 0.2s ease;
  border-radius: $radius-sm;
  z-index: 10;

  &:hover {
    color: #000;
    background-color: rgba(0, 0, 0, 0.05);
  }

  &:focus {
    outline: 2px solid #0d6efd;
    outline-offset: 2px;
  }

  svg {
    width: 24px;
    height: 24px;
  }
}

@media (max-width: 768px) {
  .cc-add-contact-dialog {
    padding: 1rem;
    max-width: 98%;
  }
}

.cc-name-group {
  margin-bottom: $spacing-4;
}

.cc-name-inputs {
  display: flex;
  gap: $gap-sm;
  align-items: flex-start;

  .cc-name-field {
    flex: 1;
  }

  @media (max-width: 767px) {
    flex-direction: column;
  }
}

.section-title {
  @include typography-body;
  @include font-weight-semibold;
  margin: 1.5rem 0 0.5rem;
}


.cc-add-contact-dialog {
  .k-textbox,
  .k-dropdown,
  select {
    @include typography-body;
    width: 100%;
    padding: 0.75rem;
    border: 1px solid #cccccc;
    border-radius: $radius-md;
    background: var(--vt-c-white);
    box-sizing: border-box;
  }

  .k-textbox:focus,
  .k-dropdown:focus,
  select:focus {
    outline: none;
    border-color: #0d6efd;
    box-shadow: 0 0 0 2px rgba(#0d6efd, 0.2);
  }
}

.checkbox-row {
  margin: 1rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.k-button {
  @include typography-body;
  @include font-weight-medium;
  padding: 0.5rem 1.25rem;
  border-radius: $radius-md;
}

.k-button.k-primary {
  background-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  border: none;
  color: white;
}

.k-button:hover {
  opacity: 0.9;
}
.form-section {
  max-width: 800px;
  margin: 0 auto;
  font-family: sans-serif;
}

.section-title {
  @include typography-h4;
  @include font-weight-bold;
  margin-bottom: 2rem;
}

.form-label {
  @include typography-body;
  @include font-weight-medium;
  margin-bottom: 0.5rem;
  display: block;
}

.required {

  margin-left: 0.25rem;
}


.button-container {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1.5rem;
  flex-wrap: wrap;
}

@media (max-width: 768px) {
  .button-container {
    flex-direction: column-reverse;
  }

  .button-container .k-button {
    width: 100%;
  }
}

.error-input {
  /* !important required: Override Kendo's .k-textbox default border to show error state */
  border-color: #dc3545 !important;
}

.k-form-error {
  color: #dc3545;
  @include typography-caption;
  margin-top: $spacing-1;
}

.required-field {
  border-color: #7e7e7e;
}

.required-field::placeholder {
  color:#7e7e7e
}

/* Address Verification Overlay */
.verify-address-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(black, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: $z-critical;
  padding: 1rem;
}

.verify-address-modal {
  background: white;
  padding: 2rem;
  border-radius: $radius-md;
  width: 600px;
  max-width: 95%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 4px 20px rgba(black, 0.2);
}

@media (max-width: 768px) {
  .verify-address-modal {
    padding: 1.5rem;
    max-width: 98%;
  }
}
</style>
